import { call, put, select, takeLatest } from 'redux-saga/effects'
import i18n from '../../../../i18n'
import { AppRouterConstants } from '../../../../components/appRouter/AppRouter.constants'
import { showToast } from '../../../../components/toast/store/toast.actions'
import { ToastColor } from '../../../../components/toast/store/toast.constants'
import { removeNotification } from '../../../notificationCenter/store/notificationCenter.actions'
import { getAllPCRDocs } from '../../../home/store/home.actions'
import { getAuthenticationJwtSelector } from '../../../login/selectors/getAuthenticationJwt.selector'
import { getUserIdSelector } from '../../../user/selectors'
import { getRouteOrigin, goBack, navigateReplace } from '../../../../utilities/navigation.util'
import { QaAddendumService } from '../services/qaAddendum.service'
import { ZollAPIService } from '../../../../services/zollApi.service'
import { ALL_CREW_MEMBERS_ID, QaAddendumConstants } from '../store/qaAddendum.constants'
import { fetchQaFlagDetail, resetReassign, setSelectedFlagId } from '../store/qaAddendum.actions'
import { getQaAddendumSelector } from '../selectors'
import { IQaAddendumReassignResult } from '../store/qaAddendum.types'
import { resetLoader, showLoader } from '../../../../components/loader/store/loader.actions'
import {
  handleApiError,
  setErrorToastDuration,
} from '../../../../components/apiErrorHandler/store/apiErrorHandler.actions'
import Logger from '../../../../services/logger.service'
import { performWithMutexHOG } from '../../../../services/mutexUtility.service'

function* reassignFlag() {
  const {
    selectedFlagId: flagId,
    reassignAssignee: assignedTo,
    reassignReason: reason,
  }: ReturnType<typeof getQaAddendumSelector> = yield select(getQaAddendumSelector)
  const origin = getRouteOrigin()
  const wasInsidePcr = !origin || origin.startsWith(AppRouterConstants.TABS_ROUTE)
  const navigationTarget = wasInsidePcr ? AppRouterConstants.HOME_ROUTE : (origin as string)
  if (!flagId || !assignedTo || !reason.trim()) return

  const isAllCrewMembers = Number(assignedTo.id) === ALL_CREW_MEMBERS_ID
  const currentUserId: string = yield select(getUserIdSelector)
  const isReassignToSelf = String(assignedTo.id) === currentUserId

  const authenticationJwt: string = yield select(getAuthenticationJwtSelector)
  const qaAddendumService = new QaAddendumService(authenticationJwt)

  yield put(showLoader({ isOpen: true, message: i18n.t('qaFlags.qaAddendum.reassign.submitting') }))

  try {
    const response: IQaAddendumReassignResult = yield call(
      [qaAddendumService, qaAddendumService.reassignQaFlag],
      flagId,
      assignedTo,
      reason
    )

    if (response.status === 200) {
      yield put(
        showToast({
          message: i18n.t('qaFlags.qaAddendum.reassign.success', { name: assignedTo.name }),
          duration: 2500,
          position: 'top',
          color: ToastColor.Success,
        })
      )

      yield put(resetReassign())

      if (isAllCrewMembers || isReassignToSelf) {
        yield put(fetchQaFlagDetail(String(flagId)))
        goBack()
      } else {
        yield put(removeNotification(Number(flagId)))
        yield put(setSelectedFlagId(null))
        yield put(getAllPCRDocs())
        navigateReplace(navigationTarget)
      }
      Logger.info(`QaAddendum: reassignFlag success, flagId: ${flagId}`)
    } else {
      response.failure = true
      if (response.messages?.length) {
        const index = response.messages.findIndex((x) => x.status === response.status)
        response.responseMessage = response.messages[index]?.message
      } else {
        response.responseMessage = i18n.t('qaFlags.qaAddendum.reassign.error')
      }
      yield put(handleApiError(response))
      navigateReplace(navigationTarget)
    }
  } catch (e: unknown) {
    const cause = e instanceof Error ? e.message : 'unknown'
    yield put(setErrorToastDuration(2000))
    yield put(
      handleApiError(
        ZollAPIService.createToastLogErrorResponse(i18n.t('qaFlags.qaAddendum.reassign.failed', { cause }))
      )
    )
    navigateReplace(navigationTarget)
  } finally {
    yield put(resetLoader())
  }
}

export function* reassignFlagSaga() {
  yield takeLatest([QaAddendumConstants.REASSIGN_QA_FLAG], performWithMutexHOG, reassignFlag)
}
